import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Lock, Upload, Trash2, X, Play, ImageIcon, LogOut, Loader2 } from "lucide-react";
import { API, SERVICE_OPTIONS } from "../../lib/site";
import { useAdminAuth } from "../../context/AdminAuth";
import { Reveal } from "../site/Reveal";

const inputClass =
  "w-full h-12 px-4 rounded-lg bg-forest border border-white/10 text-cream placeholder:text-cream/40 font-body focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange transition-colors duration-200";

const fmtBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
};

const AdminLoginModal = ({ open, onClose }) => {
  const { login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Admin.");
      onClose();
      setEmail(""); setPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[70] grid place-items-center p-5 bg-black/70 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="admin-login-modal">
          <motion.form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
            className="w-full max-w-md rounded-2xl border border-white/10 bg-forest-surface p-7">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display uppercase tracking-tight text-2xl text-cream flex items-center gap-2"><Lock className="h-5 w-5 text-brand-orange" /> Admin Login</h3>
              <button type="button" onClick={onClose} className="text-cream/60 hover:text-cream" data-testid="admin-login-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <input className={inputClass} type="email" placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" />
              <input className={inputClass} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
            </div>
            <button type="submit" disabled={busy} className="mt-5 w-full h-12 rounded-full bg-brand-orange hover:bg-brand-orangeHover disabled:opacity-60 text-white font-display uppercase tracking-wider flex items-center justify-center gap-2 transition-colors" data-testid="admin-login-submit">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log In"}
            </button>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const UploadPanel = ({ onUploaded }) => {
  const { authHeader } = useAdminAuth();
  const [mode, setMode] = useState("device");
  const [service, setService] = useState(SERVICE_OPTIONS[0]);
  const [caption, setCaption] = useState("");
  const [files, setFiles] = useState([]);
  const [driveLinks, setDriveLinks] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const MAX_FILE = 200 * 1024 * 1024;

  const submitDevice = async (e) => {
    const tooBig = files.filter((f) => f.size > MAX_FILE);
    const valid = files.filter((f) => f.size <= MAX_FILE);
    if (tooBig.length) toast.error(`${tooBig.length} file(s) over 200MB were skipped.`);
    if (!valid.length) return;

    setBusy(true);
    setProgress({ done: 0, total: valid.length });
    let ok = 0, failed = 0;
    for (const f of valid) {
      try {
        const data = new FormData();
        data.append("service", service);
        data.append("caption", caption);
        data.append("file", f);
        await axios.post(`${API}/portfolio`, data, { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) {
        failed += 1;
        const msg = err?.response?.data?.detail;
        if (msg && msg.includes("2GB")) { toast.error("Storage limit of 2GB reached — remaining files skipped."); break; }
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    if (ok) toast.success(`${ok} file${ok !== 1 ? "s" : ""} uploaded${failed ? `, ${failed} failed` : ""}.`);
    else if (failed) toast.error("Upload failed. Please try again.");
    setFiles([]);
    setProgress({ done: 0, total: 0 });
    if (e?.target) e.target.reset();
  };

  const submitDrive = async () => {
    const links = driveLinks.split(/[\n,]+/).map((l) => l.trim()).filter(Boolean);
    if (!links.length) return toast.error("Paste at least one Google Drive link.");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/portfolio/from-drive`, { service, caption, links }, { headers: authHeader() });
      if (data.uploaded) toast.success(`${data.uploaded} file${data.uploaded !== 1 ? "s" : ""} imported from Google Drive.`);
      if (data.failed?.length) {
        toast.error(`${data.failed.length} link(s) failed: ${data.failed[0].reason}`);
      }
      if (data.uploaded) setDriveLinks("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed. Check the links and try again.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "device" && !files.length) return toast.error("Choose one or more images or videos first.");
    setBusy(true);
    try {
      if (mode === "device") await submitDevice(e);
      else await submitDrive();
    } finally {
      setBusy(false);
      setCaption("");
      onUploaded();
    }
  };

  const tabClass = (active) =>
    `h-10 px-4 rounded-full font-display uppercase tracking-wider text-xs transition-colors ${active ? "bg-brand-orange text-white" : "bg-forest text-cream/60 border border-white/10 hover:text-cream"}`;

  return (
    <form onSubmit={submit} className="rounded-2xl border border-brand-orange/30 bg-forest-surface p-6 mb-10" data-testid="portfolio-upload-panel">
      <h3 className="font-display uppercase tracking-tight text-xl text-cream mb-4 flex items-center gap-2"><Upload className="h-5 w-5 text-brand-orange" /> Add Portfolio Media</h3>

      <div className="flex gap-2 mb-4">
        <button type="button" onClick={() => setMode("device")} className={tabClass(mode === "device")} data-testid="upload-mode-device">From Device</button>
        <button type="button" onClick={() => setMode("drive")} className={tabClass(mode === "drive")} data-testid="upload-mode-drive">From Google Drive</button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <select className={inputClass} value={service} onChange={(e) => setService(e.target.value)} data-testid="upload-service">
          {SERVICE_OPTIONS.map((s) => <option key={s} value={s} className="bg-forest">{s}</option>)}
        </select>
        <input className={inputClass} placeholder="Caption (optional, applied to all)" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="upload-caption" />
      </div>

      {mode === "device" ? (
        <>
          <input type="file" accept="image/*,video/*" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} className="mt-3 block w-full text-sm text-cream/70 file:mr-4 file:h-11 file:px-5 file:rounded-full file:border-0 file:bg-brand-orange file:text-white file:font-display file:uppercase file:tracking-wider file:cursor-pointer" data-testid="upload-file" />
          <p className="mt-2 text-cream/40 text-xs">
            Select multiple photos & videos at once · max 200MB per file · 2GB total.
            {files.length > 0 && <span className="text-brand-orange"> {files.length} file{files.length !== 1 ? "s" : ""} selected.</span>}
          </p>
        </>
      ) : (
        <>
          <textarea rows={4} className={`${inputClass} h-auto py-3 mt-3 resize-none`} placeholder={"Paste Google Drive share links — one per line\nhttps://drive.google.com/file/d/FILE_ID/view?usp=sharing"} value={driveLinks} onChange={(e) => setDriveLinks(e.target.value)} data-testid="upload-drive-links" />
          <p className="mt-2 text-cream/40 text-xs">
            Each file's sharing must be set to <span className="text-cream/70">"Anyone with the link"</span>. Paste one link per line. Folder links aren't supported — use individual file links.
          </p>
        </>
      )}

      <button type="submit" disabled={busy} className="mt-4 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-brand-orange hover:bg-brand-orangeHover disabled:opacity-60 text-white font-display uppercase tracking-wider transition-colors" data-testid="upload-submit">
        {busy ? (
          mode === "device"
            ? <><Loader2 className="h-5 w-5 animate-spin" /> Uploading {progress.done}/{progress.total}</>
            : <><Loader2 className="h-5 w-5 animate-spin" /> Importing…</>
        ) : mode === "device"
          ? <><Upload className="h-4 w-4" /> Upload{files.length > 1 ? ` ${files.length} Files` : ""}</>
          : <><Upload className="h-4 w-4" /> Import from Drive</>}
      </button>
    </form>
  );
};

const MediaCard = ({ item, isAdmin, onDelete }) => {
  const src = `${API}/portfolio/${item.id}/media`;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      className="group relative overflow-hidden rounded-xl border border-white/10 bg-forest-surface"
      data-testid={`portfolio-item-${item.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        {item.media_type === "video" ? (
          <video src={src} controls preload="metadata" className="h-full w-full object-cover bg-black" />
        ) : (
          <img src={src} alt={item.caption || item.service} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        )}
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-forest/80 backdrop-blur px-3 py-1.5 text-xs font-display uppercase tracking-wider text-cream">
          {item.media_type === "video" ? <Play className="h-3.5 w-3.5 text-brand-orange" /> : <ImageIcon className="h-3.5 w-3.5 text-brand-orange" />}
          {item.media_type}
        </span>
        {isAdmin && (
          <button onClick={() => onDelete(item.id)} className="absolute top-3 right-3 grid place-items-center h-9 w-9 rounded-full bg-red-600/90 hover:bg-red-600 text-white transition-colors" data-testid={`portfolio-delete-${item.id}`} aria-label="Delete media">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="p-4">
        <p className="font-display uppercase tracking-tight text-brand-orange text-sm">{item.service}</p>
        {item.caption && <p className="mt-1 text-cream/70 text-sm">{item.caption}</p>}
      </div>
    </motion.div>
  );
};

export const Portfolio = () => {
  const { isAdmin, logout, authHeader } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loginOpen, setLoginOpen] = useState(false);
  const [storage, setStorage] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/portfolio`);
      setItems(data.items || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  const loadStorage = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await axios.get(`${API}/portfolio/storage`, { headers: authHeader() });
      setStorage(data);
    } catch { /* ignore */ }
  }, [isAdmin, authHeader]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStorage(); }, [loadStorage, items]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/portfolio/${id}`, { headers: authHeader() });
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Media deleted.");
    } catch (err) {
      toast.error("Could not delete media.");
    }
  };

  const filters = ["All", ...SERVICE_OPTIONS];
  const shown = filter === "All" ? items : items.filter((i) => i.service === filter);

  return (
    <section id="portfolio" className="relative bg-forest-deep py-24 md:py-32 scroll-mt-20 noise-overlay" data-testid="portfolio-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <span className="font-display uppercase tracking-[0.25em] text-xs text-brand-orange">Our Work</span>
            <h2 className="mt-4 font-display uppercase tracking-tight text-5xl sm:text-6xl lg:text-7xl text-cream leading-[0.95]">
              Portfolio <span className="text-brand-orange">gallery</span>
            </h2>
            <p className="mt-4 text-cream/70 leading-relaxed">A look at completed projects across our services — real photos and videos of the Bezalel team at work.</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin ? (
              <button onClick={logout} className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-white/15 text-cream/70 hover:text-cream hover:border-white/30 font-display uppercase tracking-wider text-xs transition-colors" data-testid="admin-logout">
                <LogOut className="h-4 w-4" /> Log out
              </button>
            ) : (
              <button onClick={() => setLoginOpen(true)} className="inline-flex items-center gap-2 h-11 px-4 rounded-full border border-white/15 text-cream/50 hover:text-cream hover:border-brand-orange/50 font-display uppercase tracking-wider text-xs transition-colors" data-testid="portfolio-admin-btn">
                <Lock className="h-4 w-4" /> Admin
              </button>
            )}
          </div>
        </Reveal>

        {isAdmin && storage && (
          <div className="mb-6 rounded-xl border border-white/10 bg-forest-surface p-4" data-testid="storage-bar">
            <div className="flex justify-between text-xs text-cream/70 mb-2">
              <span className="font-display uppercase tracking-wider">Storage used</span>
              <span>{fmtBytes(storage.used_bytes)} / 2 GB ({storage.percent}%)</span>
            </div>
            <div className="h-2 rounded-full bg-forest overflow-hidden">
              <div className="h-full bg-brand-orange transition-[width] duration-500" style={{ width: `${Math.min(storage.percent, 100)}%` }} />
            </div>
          </div>
        )}

        {isAdmin && <UploadPanel onUploaded={load} />}

        <div className="flex gap-2 overflow-x-auto pb-3 mb-8 -mx-1 px-1" data-testid="portfolio-filters">
          {filters.map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`shrink-0 h-10 px-4 rounded-full font-display uppercase tracking-wider text-xs transition-colors duration-200 ${filter === f ? "bg-brand-orange text-white" : "bg-forest-surface text-cream/60 hover:text-cream border border-white/10"}`}
              data-testid={`filter-${f.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}>
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center text-cream/50"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : shown.length === 0 ? (
          <div className="py-20 text-center rounded-2xl border border-dashed border-white/15" data-testid="portfolio-empty">
            <ImageIcon className="h-10 w-10 text-cream/30 mx-auto mb-3" />
            <p className="text-cream/60">{filter === "All" ? "No portfolio media yet — check back soon." : `No media for "${filter}" yet.`}</p>
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <AnimatePresence>
              {shown.map((item) => (
                <MediaCard key={item.id} item={item} isAdmin={isAdmin} onDelete={handleDelete} />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      <AdminLoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </section>
  );
};
